import React, { useState, useRef, useEffect, useContext } from "react";
import Search from "../img/Search.svg";
import notification from "../img/notification.svg"; // Current notification icon
import User from "../img/User.svg";
import Sort from "../img/Sort.svg"; // New logo for mobile (hamburger icon)
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import api from "../../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import DopePDF from "../img/MarketPlace.pdf";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const { cartCount, fetchCartCount } = useContext(CartContext);

  const getActiveTab = (path) => {
    if (path === "/myproducts") {
      return pathname.startsWith("/myproducts");
    }
    return pathname.startsWith(path);
  };

  useEffect(() => {
    fetchCartCount();
  }, []);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (value.trim()) {
        try {
          const response = await api.get(
            `/display/api/v1/list-project-for-display?context=${value}`
          );
          if (response.data?.status === "success") {
            setSearchResults(response.data.data.results);
            setShowDropdown(true);
          } else {
            setSearchResults([]);
            setShowDropdown(false);
          }
        } catch (error) {
          console.error("Search failed:", error);
          setSearchResults([]);
          setShowDropdown(false);
        }
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300); // debounce for 300ms
  };

  const fetchSearchResults = async (context) => {
    try {
      const response = await api.get(
        "/display/api/v1/list-project-for-display",
        {
          params: { context }, // this becomes ?context=value
        }
      );

      if (response.data?.status === "success") {
        setSearchResults(response.data.data.results || []);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  const handleResultClick = (project) => {
    navigate("/AppDetails", { state: { id: project.id } });
    setSearchText("");
    setSearchResults([]);
  };

  // Navigate to internal guidelines viewer
  const handleClickGuidelines = () => {
    setIsMobileMenuOpen(false);
    navigate("/guidelines");
  };

  return (
    <div className="w-full h-16 sticky top-0 z-50 flex items-center justify-between px-5 sm:px-10 bg-[#4C3A68] ">
      {/* Left Section: Logo and Search Bar */}
      <div className="flex items-center gap-4 flex-1">
        <span
          className="font-bold text-xl sm:text-2xl text-white whitespace-nowrap cursor-pointer"
          onClick={() => navigate("/dashboard")}
        >
          Onzitr Marketplace
        </span>

        {/* Search Bar (hidden on small screens) */}
        <div className="relative hidden md:block w-40 md:w-72">
          <img
            src={Search}
            alt="Search"
            className="absolute left-2 w-4 h-4 top-2"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)} // allow item click
            className="w-full focus:outline-none rounded-md pl-8 pr-2 py-1.5 text-sm bg-[#423756] font-semibold border border-[#4E4958] shadow-sm shadow-black/25 text-white placeholder-gray-300"
          />
          {showDropdown && (
            <div className="absolute top-10 left-0 right-0 bg-white text-black z-10 border border-gray-300 rounded shadow max-h-60 overflow-y-auto">
              {searchResults.map((project) => (
                <div
                  key={project.id}
                  onMouseDown={() => {
                    setSearchTerm(project.name);
                    setShowDropdown(false);
                    navigate("/AppDetails", { state: { id: project.id } });
                  }}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {project.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Hamburger Icon */}
      <div className="sm:hidden flex items-center">
        <span onClick={() => navigate("/cart")} class="text-white px-1 text-xs">
          Cart{" "}
          <span className="bg-[#9367D7] text-white text-xs rounded-full w-4 text-center px-1 ">
            {cartCount}
          </span>
        </span>
        <button
          onClick={toggleMobileMenu}
          className="text-white focus:outline-none"
        >
          <img
            src={Sort} // New logo for mobile
            alt="Hamburger Menu"
            className="w-6 h-6"
          />
        </button>
      </div>

      {/* new */}

      <div className="hidden sm:flex items-center gap-3 sm:gap-5 text-white text-xs sm:text-sm flex-1 justify-end">
        {[
          { path: "/dashboard", label: "Home" },
          { path: "/products", label: "Products" },
          { path: "/cart", label: "Cart" },
          { path: "/myproducts", label: "My Products" },
        ].map(({ path, label }) => (
          <span
            key={path}
            onClick={() => {
              navigate(path);
              setIsMobileMenuOpen(false); // if you also want to close a mobile menu
            }}
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                navigate(path);
                setIsMobileMenuOpen(false);
              }
            }}
            className={` border-b-2 transition relative cursor-pointer ${
              getActiveTab(path)
                ? "border-[#9061DB] font-semibold"
                : "border-transparent hover:border-[#9061DB]"
            }`}
          >
            {label}
            {path === "/cart" && cartCount > 0 && (
              <span className="absolute transform -translate-y-1/2 bg-[#9367D7] text-white text-xs rounded-full w-4 text-center">
                {cartCount}
              </span>
            )}
          </span>
        ))}

        {/* Guidelines as clickable span opening PDF */}
        <span
          onClick={handleClickGuidelines}
          role="link"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") handleClickGuidelines();
          }}
          className="cursor-pointer font-normal border-b-2 border-transparent hover:border-[#9061DB] transition duration-200"
          style={{ fontSize: "0.875rem" }}
        >
          Guidelines
        </span>

        {/* Profile Icon */}
        <NavLink to="/profile">
          {({ isActive }) => (
            <div
              className={`rounded-full p-2 transition ${
                isActive ? "bg-[#5D4D76]" : "hover:bg-[#5D4D76]"
              }`}
            >
              <img src={User} alt="Profile" className="h-6 w-6" />
            </div>
          )}
        </NavLink>
      </div>

      {/* Mobile Menu Links (hidden on desktop) */}

      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } sm:hidden absolute top-16 left-0 w-full bg-[#4C3A68] text-white z-10 p-5 shadow-md`}
      >
        <div className="flex flex-col gap-4">
          {[
            { path: "/dashboard", label: "Dashboard" },
            { path: "/products", label: "Products" },
            { path: "/cart", label: "Cart" },
            { path: "/myproducts", label: "My Products" },
          ].map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              className={`pb-1 border-b-2 transition relative ${
                getActiveTab(path)
                  ? "border-white font-semibold"
                  : "border-transparent hover:border-gray-300"
              }`}
            >
              {label}
              {path === "/cart" && cartCount > 0 && (
                <span className="absolute bg-[#9367D7] text-white text-xs rounded-full w-4 text-center ">
                  {cartCount}
                </span>
              )}
            </NavLink>
          ))}

          {/* Guidelines as clickable span opening PDF, aligned flush left */}
          <span
            onClick={() =>
              window.open(DopePDF, "_blank", "noopener,noreferrer")
            }
            role="link"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                window.open(DopePDF, "_blank", "noopener,noreferrer");
              }
            }}
            className="pb-1 border-transparent font-normal text-white cursor-pointer select-none hover:border-gray-300"
            style={{ fontSize: "0.875rem" }}
          >
            Guidelines
          </span>

          {/* <NavLink to="/profile">
            {({ isActive }) => (
              <div
                className={`rounded-full p-2 transition ${
                  isActive ? "bg-[#5D4D76]" : "hover:bg-[#5D4D76]"
                }`}
              >
                <img src={User} alt="Profile" className="h-6 w-6" />
              </div>
            )}
          </NavLink> */}

          <NavLink to="/profile">
            {({ isActive }) => (
              <div
                className={`inline-block border-b-2  w-full pb-1 ${
                  isActive
                    ? "border-white font-semibold"
                    : "border-transparent hover:border-gray-300"
                }`}
              >
                Profile
              </div>
            )}
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default Header;
